import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash, MapPin, ShieldCheck, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SectionHeader, ConfirmDialog } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { DataTable } from "../components/DataTable";

export default function Locations() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "" });
  const [editId, setEditId] = useState(null);
  const [delLoc, setDelLoc] = useState(null);

  const { data: locations = [] } = useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get("/locations")).data });

  const save = useMutation({
    mutationFn: async () => {
      if (editId) return (await api.put(`/locations/${editId}`, form)).data;
      return (await api.post("/locations", form)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["locations"] }); setOpen(false); setForm({ code: "", name: "" }); setEditId(null); toast.success("Location saved"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/locations/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["locations"] }); toast.success("Location removed"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (user?.role !== "admin") {
    return (
      <div className="animate-fade-up border border-border bg-card p-10 text-center">
        <ShieldCheck size={40} className="mx-auto text-muted-foreground mb-3" />
        <h2 className="text-lg font-bold">Administrators only</h2>
        <p className="text-sm text-muted-foreground mt-1">You need the admin role to manage locations.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-up">
      <SectionHeader title="Locations" subtitle="Warehouses & sites">
        <Button onClick={() => { setForm({ code: "", name: "" }); setEditId(null); setOpen(true); }} data-testid="new-location-button" className="rounded-sm bg-primary hover:bg-primary/90 h-9">
          <Plus size={16} weight="bold" className="mr-1" /> New Location
        </Button>
      </SectionHeader>

      <DataTable
        testid="locations-table"
        data={locations}
        rowKey={(l) => l.id}
        rowTestid="location-row"
        empty="No locations yet."
        columns={[
          { key: "code", header: "Code", filter: "text", cellClassName: "font-mono text-xs font-medium" },
          { key: "name", header: "Name", filter: "text", cellClassName: "text-sm font-medium", cell: (l) => l.name || "—" },
          { key: "actions", header: "", sortable: false, align: "right", cell: (l) => (
            <div className="flex justify-end gap-1">
              <button onClick={() => { setForm({ code: l.code, name: l.name || "" }); setEditId(l.id); setOpen(true); }} className="p-1.5 text-muted-foreground hover:text-primary" data-testid="edit-location"><PencilSimple size={16} /></button>
              <button onClick={() => setDelLoc(l)} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid="delete-location"><Trash size={16} /></button>
            </div>
          ) },
        ]}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MapPin size={20} className="text-primary" /> {editId ? "Edit" : "New"} Location</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="overline-label">Code</Label><Input data-testid="location-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1 rounded-sm" placeholder="e.g. MAIN" /></div>
            <div><Label className="overline-label">Name</Label><Input data-testid="location-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" placeholder="e.g. Main Warehouse" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!form.code.trim() || save.isPending} data-testid="save-location-button" className="rounded-sm bg-primary hover:bg-primary/90">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delLoc}
        onOpenChange={(v) => { if (!v) setDelLoc(null); }}
        title="Delete this location?"
        description={delLoc ? `${delLoc.code} will be removed. Documents already using it keep their saved code.` : ""}
        confirmLabel="Delete"
        testid="location-delete-confirm"
        onConfirm={() => { if (delLoc) del.mutate(delLoc.id); setDelLoc(null); }}
      />
    </div>
  );
}
